import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PF, fmtEur, currentMonth, AMBITOS, SUBCATS_MENINAS } from './pfApi';
import { useConfirmDelete } from '@/components/ConfirmDialogs';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const emptyForm = { type: 'despesa', amount: '', date: new Date().toISOString().slice(0, 10), category_id: '', account_id: '', note: '', ambito: '', essencial: true, subcategoria_meninas: '', percentagem_duarte: '', despesa_atividade: true };

export default function PfQuickAdd({ categories, accounts }) {
  const confirmDelete = useConfirmDelete();
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [recent, setRecent] = useState([]);

  const fetchRecent = useCallback(async () => {
    try { const { data } = await axios.get(`${PF}/movements`, { params: { month: currentMonth() } }); setRecent(data.slice(0, 15)); } catch {}
  }, []);
  useEffect(() => { fetchRecent(); }, [fetchRecent]);

  const submit = async () => {
    const amount = parseFloat(String(form.amount).replace(',', '.'));
    if (!amount || amount <= 0) { toast.error('Indica um valor válido'); return; }
    if (!form.ambito) { toast.error('Escolhe o âmbito'); return; }
    setSaving(true);
    try {
      const payload = { ...form, amount, percentagem_duarte: form.percentagem_duarte === '' ? undefined : parseFloat(form.percentagem_duarte) };
      await axios.post(`${PF}/movements`, payload);
      toast.success('Movimento registado');
      setForm({ ...emptyForm, type: form.type });
      fetchRecent();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao registar'); }
    setSaving(false);
  };

  const del = async (id) => {
    const m = recent.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar o movimento "${m?.note || (m?.type === 'despesa' ? 'Despesa' : 'Receita')}" de ${m?.date || ''}.` });
    if (!ok) return;
    try { await axios.delete(`${PF}/movements/${id}`); toast.success('Eliminado'); fetchRecent(); } catch { toast.error('Erro'); }
  };

  const filteredCats = categories.filter(c => c.type === form.type);

  return (
    <div data-testid="pf-quickadd-tab">
      <div className="bg-white border border-brand-border rounded-2xl p-4 mb-5">
        <div className="flex gap-2 mb-4">
          <button onClick={() => setForm(f => ({ ...f, type: 'despesa', category_id: '' }))} className={`flex-1 py-2.5 rounded-xl text-sm font-medium ${form.type === 'despesa' ? 'bg-brand-text text-white' : 'bg-brand-bg text-brand-muted'}`} data-testid="pf-quickadd-type-despesa">Despesa</button>
          <button onClick={() => setForm(f => ({ ...f, type: 'receita', category_id: '' }))} className={`flex-1 py-2.5 rounded-xl text-sm font-medium ${form.type === 'receita' ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted'}`} data-testid="pf-quickadd-type-receita">Receita</button>
        </div>

        <Label className="text-xs text-brand-muted mb-1 block">Valor (€)</Label>
        <Input inputMode="decimal" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
          placeholder="0.00" className="rounded-xl border-brand-border text-2xl h-14 font-heading font-semibold mb-3" data-testid="pf-quickadd-amount" />

        <div className="grid grid-cols-2 gap-2 mb-3">
          <div>
            <Label className="text-xs text-brand-muted mb-1 block">Data</Label>
            <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="rounded-xl border-brand-border" data-testid="pf-quickadd-date" />
          </div>
          <div>
            <Label className="text-xs text-brand-muted mb-1 block">Categoria</Label>
            <Select value={form.category_id} onValueChange={v => setForm(f => ({ ...f, category_id: v }))}>
              <SelectTrigger className="rounded-xl border-brand-border" data-testid="pf-quickadd-category"><SelectValue placeholder="Escolher" /></SelectTrigger>
              <SelectContent>{filteredCats.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <Label className="text-xs text-brand-muted mb-1 block">Âmbito</Label>
        <Select value={form.ambito} onValueChange={v => setForm(f => ({ ...f, ambito: v, subcategoria_meninas: '', percentagem_duarte: v === 'Meninas' ? '50' : f.percentagem_duarte, despesa_atividade: v === 'Profissional' }))}>
          <SelectTrigger className="rounded-xl border-brand-border mb-3" data-testid="pf-quickadd-ambito"><SelectValue placeholder="Escolher âmbito" /></SelectTrigger>
          <SelectContent>{AMBITOS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
        </Select>

        {form.ambito === 'Meninas' && (
          <>
            <Label className="text-xs text-brand-muted mb-1 block">Subcategoria (Meninas)</Label>
            <Select value={form.subcategoria_meninas} onValueChange={v => setForm(f => ({ ...f, subcategoria_meninas: v }))}>
              <SelectTrigger className="rounded-xl border-brand-border mb-3" data-testid="pf-quickadd-subcategoria-meninas"><SelectValue placeholder="Escolher" /></SelectTrigger>
              <SelectContent>{SUBCATS_MENINAS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </>
        )}

        <div className="grid grid-cols-2 gap-2 mb-3">
          <div>
            <Label className="text-xs text-brand-muted mb-1 block">% Duarte</Label>
            <Input type="number" min="0" max="100" value={form.percentagem_duarte} onChange={e => setForm(f => ({ ...f, percentagem_duarte: e.target.value }))} placeholder={form.ambito === 'Meninas' ? '50' : '0'} className="rounded-xl border-brand-border" data-testid="pf-quickadd-percentagem-duarte" />
          </div>
          {form.type === 'despesa' && (
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Essencial</Label>
              <button type="button" onClick={() => setForm(f => ({ ...f, essencial: !f.essencial }))} className={`w-full py-2 rounded-xl text-sm font-medium ${form.essencial ? 'bg-brand-green/10 text-brand-green' : 'bg-brand-bg text-brand-muted'}`} data-testid="pf-quickadd-essencial-toggle">
                {form.essencial ? 'Essencial' : 'Dispensável'}
              </button>
            </div>
          )}
        </div>

        {form.ambito === 'Profissional' && form.type === 'despesa' && (
          <label className="flex items-center gap-2 text-sm text-brand-text cursor-pointer mb-3">
            <input type="checkbox" checked={form.despesa_atividade} onChange={e => setForm(f => ({ ...f, despesa_atividade: e.target.checked }))} data-testid="pf-quickadd-despesa-atividade" /> Despesa da atividade (com fatura e-fatura)
          </label>
        )}

        <Label className="text-xs text-brand-muted mb-1 block">Conta</Label>
        <Select value={form.account_id} onValueChange={v => setForm(f => ({ ...f, account_id: v }))}>
          <SelectTrigger className="rounded-xl border-brand-border mb-3" data-testid="pf-quickadd-account"><SelectValue placeholder="Escolher conta" /></SelectTrigger>
          <SelectContent>{accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
        </Select>

        <Label className="text-xs text-brand-muted mb-1 block">Nota (opcional)</Label>
        <Input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="Ex: Continente" className="rounded-xl border-brand-border mb-4" data-testid="pf-quickadd-note" />

        <button onClick={submit} disabled={saving} className="w-full bg-brand-green text-white rounded-full py-3 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50" data-testid="pf-quickadd-submit">
          <Plus size={16} /> Registar {form.type === 'despesa' ? 'despesa' : 'receita'}
        </button>
      </div>

      <p className="text-xs font-medium text-brand-muted mb-2">Últimos movimentos deste mês</p>
      <div className="space-y-1.5">
        {recent.map(m => (
          <div key={m.id} className="bg-white border border-brand-border rounded-xl p-3 flex items-center justify-between gap-2" data-testid={`pf-recent-${m.id}`}>
            <div className="min-w-0">
              <p className="text-sm text-brand-text truncate">{m.note || (m.type === 'despesa' ? 'Despesa' : 'Receita')}</p>
              <p className="text-[11px] text-brand-muted">{fmtDatePt(m.date)}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`text-sm font-medium ${m.type === 'despesa' ? 'text-red-600' : 'text-brand-green'}`}>{m.type === 'despesa' ? '-' : '+'}{fmtEur(m.amount)}</span>
              <button onClick={() => del(m.id)} className="text-brand-placeholder hover:text-red-500 p-1" data-testid={`pf-recent-delete-${m.id}`}><Trash size={14} /></button>
            </div>
          </div>
        ))}
        {recent.length === 0 && <p className="text-sm text-brand-placeholder">Sem movimentos ainda.</p>}
      </div>
    </div>
  );
}
